import { ButtonProps } from "@mui/material/Button/Button";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useCallback, useMemo, useRef, useState } from "react";
import { Dialog } from "../../../components/Dialog";
import { useStyles } from "../styles";
import { map } from "lodash";
import { Type } from "../../../components/Offers/Type";
import { CreateOfferData } from "../../../types/offers";
import { Infos } from "../../../components/Offers/Infos";
import { addDays } from "date-fns";
import { Products } from "../../../components/Offers/Products";
import { Review } from "../../../components/Offers/Review";
import { useMutation } from "react-query";
import { newOffers } from "../../../services/offers";
import { useToast } from "../../../hooks/useToast";
import { queryClient } from "../../../services/queryClient";

type NewOffersProps = {
  open: boolean;
  onClose: () => void;
};

const validationSchema = Yup.object().shape({
  type: Yup.string().nullable().required("Tipo da oferta inválido"),
  name: Yup.string().nullable().required("Nome obrigatório"),
  startDate: Yup.date().nullable().required("Data de início inválido."),
  endDate: Yup.date().nullable().required("Data de término inválido."),
  insight: Yup.object()
    .nullable()
    .when("type", {
      is: (type) => {
        return type === "personalized";
      },
      then: (schema) => {
        return schema.required("Selecione uma audiência.");
      },
    }),
  clientOne2One: Yup.object()
    .nullable()
    .when("type", {
      is: (type) => {
        return type === "one_to_one";
      },
      then: (schema) => {
        return schema.required("Selecione uma pessoa.");
      },
    }),
  stores: Yup.array()
    .of(Yup.object().nullable())
    .min(1, "Selecione ao menos uma loja.")
    .required("Selecione ao menos uma loja."),
  products: Yup.array()
    .of(Yup.object().nullable())
    .min(1, "Selecione ao menos um produto.")
    .required("Selecione ao menos um produto."),
});

export function NewOffers({ onClose, open }: NewOffersProps) {
  const formikRef = useRef<FormikProps<CreateOfferData>>(null);
  const toast = useToast();

  const styles = useStyles();
  const [activeStep, setActiveStep] = useState<number>(0);

  const handleClose = useCallback(() => {
    setActiveStep(0);
    onClose();
  }, [onClose, setActiveStep]);

  const createOffers = useMutation(
    async (data: CreateOfferData) => {
      const response = await newOffers(data);
      return response;
    },
    {
      onSuccess: () => {
        toast.show("Oferta criada com sucesso!", { severity: "success" });
        queryClient.invalidateQueries(["offers"]);
        handleClose();
      },
      onError: () => {
        toast.show("Erro ao criar a oferta", { severity: "error" });
      },
    }
  );

  const steps = useMemo(() => {
    return ["Tipo", "Informações", "Produtos", "Resumo"];
  }, []);

  const handleSave = useCallback(
    async (data: CreateOfferData) => {
      await createOffers.mutateAsync(data);
    },
    [createOffers]
  );

  const handleValidateStep = useCallback((): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      try {
        switch (activeStep) {
          case 0:
            await formikRef.current?.validateField("type");
            break;
          case 1:
            await formikRef.current?.validateField("name");
            await formikRef.current?.validateField("clientOne2One");
            await formikRef.current?.validateField("insight");
            await formikRef.current?.validateField("stores");
            await formikRef.current?.validateField("startDate");
            await formikRef.current?.validateField("endDate");
            break;
          case 2:
            await formikRef.current?.validateField("products");
            break;
        }

        resolve(formikRef.current?.isValid);
      } catch (err) {
        reject(false);
      }
    });
  }, [activeStep, formikRef]);

  const handleGoBack = useCallback(() => {
    formikRef.current?.setErrors({});
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleGoNext = useCallback(async () => {
    try {
      const isValid = await handleValidateStep();
      if (isValid) {
        setActiveStep((prev) => prev + 1);
      }
    } catch (err) {
      console.log(err);
    }
  }, [handleValidateStep]);

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Voltar",
        onClick: () => handleGoBack(),
        disabled: createOffers.isLoading || activeStep === 0,
      },
      {
        disabled: createOffers.isLoading,
        children: createOffers.isLoading
          ? "Enviado solicitação..."
          : activeStep === 3
          ? "Criar"
          : "Continuar",

        variant: "contained",
        onClick: () =>
          activeStep === 3 ? formikRef.current?.handleSubmit() : handleGoNext(),
      },
    ];
  }, [activeStep, createOffers, formikRef, handleGoNext, handleGoBack]);

  const renderContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return <Type />;
      case 1:
        return <Infos />;
      case 2:
        return <Products />;
      case 3:
        return <Review />;
      default:
        return <></>;
    }
  }, [activeStep]);

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      title="Novo desconto personalizado"
      open={open}
      actions={actions}
      onClose={handleClose}
    >
      <Stepper activeStep={activeStep}>
        {map(steps, (step) => {
          return (
            <Step key={step}>
              <StepLabel>{step}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <div className={styles.rootContent}>
        <Formik
          innerRef={formikRef}
          initialValues={
            {
              type: "personalized",
              name: null,
              startDate: new Date(),
              endDate: addDays(new Date(), 7),
              products: [],
              stores: [],
            } as CreateOfferData
          }
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSave}
        >
          {renderContent()}
        </Formik>
      </div>
    </Dialog>
  );
}
